import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Alert,
  Badge,
  Button,
  ConfirmDialog,
  Drawer,
  DrawerFooter,
  ErrorBanner,
  Field,
  InfoTip,
  LoadingButton,
  LocalSearchField,
  SearchCombobox,
  useToast,
} from '@zarya/ui';
import type { ApiError } from '@app/api/ApiError';
import { hasKey, t, tn } from '@app/i18n';
import {
  gatewayName,
  useConfirmResourceImport,
  useInfraConfig,
  useResourceImportPreview,
  useUngovernedResources,
  type ImportPreviewItem,
  type UngovernedApi,
} from './Resources.api';
import { useSiteOptions } from '../Sites/Sites.api';
import s from './Resources.module.css';

/**
 * T8 — drawer **Importar do gateway** (FR-026, FR-027).
 *
 * Trazer para o catálogo o que o gateway do cliente expõe, com
 * pré-visualização: nada entra sem que a pessoa veja antes o que é novo, o que
 * já existe e o que sumiu do gateway. O que sumiu **não é excluído** — fica com
 * aviso, porque um recurso removido do gateway ainda pode estar concedido em
 * contrato.
 */

/**
 * Rótulos por valor do contrato, do catálogo. São funções — tabela de módulo é
 * montada na importação e ficaria presa ao idioma daquele instante.
 */
function kindLabel(kind: ImportPreviewItem['kind']): string {
  return t(`resources.import.kind.${kind}`);
}

const STATUS_TOM: Record<ImportPreviewItem['situation'], 'primary' | 'neutral' | 'warning'> = {
  novo: 'primary',
  ja_existe: 'neutral',
  removido_do_gateway: 'warning',
};

function statusLabel(situation: ImportPreviewItem['situation']): string {
  return t(`resources.import.status.${situation}`);
}

/** O que sumiu do gateway é mantido: não há o que importar dele. */
const importavel = (item: ImportPreviewItem) => item.situation !== 'removido_do_gateway';

/**
 * As mudanças do gateway que a importação registra, escolhidas ou não (aim-6ph.19, decisões do mrolim de 11/10/2026):
 * a saída que o produto ainda não marcou e a volta que ainda está marcada. É a mesma conta do servidor
 * (`gatewayChanges`, em `usecases/resources/import.go`).
 */
const exitToRecord = (item: ImportPreviewItem) =>
  item.situation === 'removido_do_gateway' && !item.removed_from_gateway;
const returnToRecord = (item: ImportPreviewItem) =>
  item.situation === 'ja_existe' && item.removed_from_gateway;

/**
 * Os avisos da linha (aim-uig.40 §5.2): os problemas de cadastro que o executor achou e a
 * leitura das tools que falhou. O código é do contrato do executor e fica byte a byte na
 * chave; um código que o catálogo ainda não conhece cai na frase genérica, com o código
 * dentro — a tela não esconde um aviso só porque ele é novo.
 */
function lineWarnings(item: ImportPreviewItem): { code: string; text: string }[] {
  const issues = (item.pattern_issues ?? []).map((code) => ({
    code,
    text: hasKey(`resources.import.patternIssue.${code}`)
      ? t(`resources.import.patternIssue.${code}`)
      : t('resources.import.patternIssue.unknown', { code }),
  }));
  const itemsError = item.items_error
    ? [
        {
          code: item.items_error,
          text: hasKey(`resources.import.itemsError.${item.items_error}`)
            ? t(`resources.import.itemsError.${item.items_error}`)
            : t('resources.import.itemsError.unknown', { code: item.items_error }),
        },
      ]
    : [];
  return [...issues, ...itemsError];
}

/** O tipo de uma API fora do padrão: `mcp` (sempre nativa fora de mcp/) ou `rest`. */
function ungovernedKindLabel(api: UngovernedApi): string {
  return t(`resources.import.ungoverned.kind.${api.kind}`);
}

/**
 * **Fora do padrão** (aim-uig.40 §5.6, protótipo aprovado pelo lead na delegação de
 * 07/10): as APIs do gateway fora de api/, mcp/ e a2a/, sem caixa de seleção — elas não
 * entram no catálogo —, e o modo em que a chamada de um agente a elas está.
 *
 * Os estados: um executor anterior à policy global responde `rota_ausente`, e a seção não
 * aparece (a prévia segue igual a antes); uma leitura que falhou diz que falhou, em vez de
 * parecer «não há nenhuma»; nenhuma API fora do padrão não mostra nada.
 */
function UngovernedSection({ siteId }: { siteId: string | null }) {
  const ungoverned = useUngovernedResources(siteId);

  if (ungoverned.isPending) return null;
  if (ungoverned.isError) {
    if (ungoverned.error.code === 'rota_ausente') return null;
    return (
      <p className={s.foraDoPadraoErro} data-testid="fora-do-padrao-erro">
        {t('resources.import.ungoverned.unreadable', { code: ungoverned.error.code })}
      </p>
    );
  }
  const reading = ungoverned.data;
  if (!reading.configured || !reading.ok) {
    return (
      <p className={s.foraDoPadraoErro} data-testid="fora-do-padrao-erro">
        {t('resources.import.ungoverned.unreadable', { code: reading.error ?? '—' })}
      </p>
    );
  }
  if (reading.apis.length === 0) return null;

  // O rótulo do modo é o MESMO da opção em T20: uma fonte só para «Bloquear e registrar».
  const mode = t(`infrastructure.config.field.ungoverned_resources.option.${reading.mode}`);
  return (
    <details className={s.foraDoPadrao} data-testid="fora-do-padrao" data-mode={reading.mode}>
      <summary className={s.foraDoPadraoTitulo}>
        {t('resources.import.ungoverned.title', { n: reading.apis.length })}{' '}
        <InfoTip text={t('resources.import.ungoverned.tip')} size="xs" />
      </summary>
      <table className={s.previa}>
        <caption className={s.previaCaption}>{t('resources.import.ungoverned.caption')}</caption>
        <thead>
          <tr>
            <th scope="col">{t('resources.import.column.identifier')}</th>
            <th scope="col">{t('resources.import.column.kind')}</th>
            <th scope="col">{t('resources.import.ungoverned.column.path')}</th>
            <th scope="col">{t('resources.import.column.tools')}</th>
          </tr>
        </thead>
        <tbody>
          {reading.apis.map((api) => (
            <tr key={api.id} data-testid={`fora-${api.id}`} data-kind={api.kind}>
              <td className={s.mono}>{api.id}</td>
              <td>{ungovernedKindLabel(api)}</td>
              <td className={s.mono}>{api.path}</td>
              {/* Sem tools: elas não vão ao catálogo, então não há o que contar. */}
              <td>—</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className={s.foraDoPadraoNota}>
        {t('resources.import.ungoverned.explain')}{' '}
        <span data-testid="fora-do-padrao-modo">
          {t('resources.import.ungoverned.mode', { mode })}
        </span>{' '}
        <Link to="/infra">{t('resources.import.ungoverned.openInfra')}</Link>
      </p>
    </details>
  );
}

export interface ImportDrawerProps {
  onClose: () => void;
}

export function ImportDrawer({ onClose }: ImportDrawerProps) {
  const infra = useInfraConfig();
  const preview = useResourceImportPreview();
  const confirm = useConfirmResourceImport();
  const toast = useToast();

  // Vazio em tenant single-site (`multi` falso), e aí nada muda na tela — a
  // revelação progressiva da P1: com um cluster só, a palavra «site» não aparece.
  const sites = useSiteOptions();
  const [site, setSite] = useState<string | null>(null);
  const [selecionados, setSelecionados] = useState<string[]>([]);
  /** Filtro por identificador (ajustes-front #1): com muitos MCPs a grade não cabe. */
  const [filter, setFiltro] = useState('');
  const [erro, setErro] = useState<ApiError | null>(null);
  /** Qual das duas mutações falhou — decide título, sufixo e o que o retry refaz. */
  const [falhouAoImportar, setFalhouAoImportar] = useState(false);
  /** A confirmação do gateway vazio está aberta (aim-6ph.19, D2). */
  const [confirmingEmpty, setConfirmingEmpty] = useState(false);
  // As APIs fora do padrão do MESMO site: a confirmação do gateway vazio diz quantas são, porque é o caso em que o
  // gateway ainda tem os recursos no caminho antigo. A chave é a da seção «Fora do padrão», e a leitura é uma só.
  const ungoverned = useUngovernedResources(site);

  const { mutateAsync: readGateway } = preview;

  // A leitura do gateway acontece ao abrir E A CADA TROCA DE SITE: a prévia é o
  // estado de agora daquele cluster. Trocar o site sem reler deixaria a grade a
  // descrever o gateway anterior — e o confirm gravaria a escolha feita sobre a
  // lista errada, que é o defeito inteiro do P1-d com outra roupa.
  //
  // A troca também limpa a seleção: um `gateway_id` marcado no cluster A pode
  // existir no B e significar outra coisa (é justamente o que a chave por site
  // passou a permitir).
  useEffect(() => {
    let live = true;
    setSelecionados([]);
    readGateway(site)
      .then(() => {
        if (live) setErro(null);
      })
      .catch((falha: ApiError) => {
        if (!live) return;
        setFalhouAoImportar(false);
        setErro(falha);
      });
    return () => {
      live = false;
    };
  }, [readGateway, site]);

  const itens = preview.data?.items ?? [];
  // aim-h4n: where the list of APIs of the gateway stopped, when it did not come whole. The preview shows what came,
  // with no line «removido do gateway», and the confirmation marks nothing as removed.
  const listing = preview.data?.gateway_listing ?? null;
  const available = itens.filter(importavel);
  const escolhidos = selecionados.filter((id) => available.some((item) => item.gateway_id === id));
  const term = filter.trim().toLowerCase();
  const filtrando = term !== '';
  const visiveis = filtrando
    ? itens.filter((item) => item.gateway_id.toLowerCase().includes(term))
    : itens;
  const visiveisImportaveis = visiveis.filter(importavel);
  // As mudanças do gateway a registrar (aim-6ph.19, D1 e D3): elas ativam o botão mesmo sem nada marcado.
  const changes = itens.filter((item) => exitToRecord(item) || returnToRecord(item)).length;
  // O gateway sem nenhum recurso, e o console com recursos dele: todas as linhas são saídas.
  const gatewayEmpty = itens.length > 0 && itens.every((item) => item.situation === 'removido_do_gateway');
  const exits = itens.filter(exitToRecord).length;
  const ungovernedCount =
    ungoverned.data?.configured && ungoverned.data.ok ? ungoverned.data.apis.length : 0;
  // Sem nada marcado, o botão registra só as mudanças, e diz isso no rótulo.
  const recordingOnly = escolhidos.length === 0 && changes > 0;

  /** "marcar todos" age sobre o que está na tela: filtrado → só os filtrados, somados aos já marcados. */
  function markVisiveis() {
    setSelecionados((current) => [
      ...current,
      ...visiveisImportaveis.map((item) => item.gateway_id).filter((id) => !current.includes(id)),
    ]);
  }

  function alternar(gatewayID: string) {
    setSelecionados((current) =>
      current.includes(gatewayID)
        ? current.filter((id) => id !== gatewayID)
        : [...current, gatewayID],
    );
  }

  /** O botão principal: com o gateway vazio, a confirmação vem antes (D2); no resto, como sempre. */
  function onPrimary() {
    if (recordingOnly && gatewayEmpty) {
      setConfirmingEmpty(true);
      return;
    }
    void confirmImport();
  }

  async function confirmImport() {
    setConfirmingEmpty(false);
    setErro(null);
    try {
      // O MESMO site da prévia, sempre: é o único jeito de a gravação descrever o
      // gateway que a pessoa viu.
      const resultado = await confirm.mutateAsync({ gatewayIds: escolhidos, siteId: site });
      toast.mostrar(
        t('resources.import.done', {
          new: resultado.new,
          updated: resultado.updated,
          removed: resultado.removed_kept,
        }),
        { tone: 'sucesso' },
      );
      onClose();
    } catch (falha) {
      setFalhouAoImportar(true);
      setErro(falha as ApiError);
    }
  }

  return (
    <Drawer
      open
      title={t('resources.import.title')}
      subtitle={
        <span className={s.drawerBadges}>
          {gatewayName(infra.data) ?? t('resources.import.gatewayUnknown')}
        </span>
      }
      onClose={onClose}
      footer={
        <DrawerFooter
          start={
            <>
              <Button
                variant="out"
                size="sm"
                disabled={visiveisImportaveis.length === 0}
                onClick={markVisiveis}
              >
                {filtrando
                  ? t('resources.import.markFiltered', { n: visiveisImportaveis.length })
                  : t('resources.import.markAll')}
              </Button>
              <Button
                variant="out"
                size="sm"
                disabled={escolhidos.length === 0}
                onClick={() => setSelecionados([])}
              >
                {t('resources.import.clear')}
              </Button>
            </>
          }
        >
          <Button variant="out" onClick={onClose}>
            {t('resources.import.close')}
          </Button>
          <LoadingButton
            variant="primary"
            data-testid="confirmar-importacao"
            // O rótulo carrega o contador (3.2); o E2E lê a contagem daqui em
            // vez de casar uma regex contra o texto do botão.
            data-selecionados={escolhidos.length}
            // As mudanças do gateway que este clique registra (aim-6ph.19): o E2E lê daqui, e não do rótulo.
            data-mudancas={changes}
            loading={confirm.isPending}
            loadingLabel={t('resources.import.confirm.loading')}
            disabled={escolhidos.length === 0 && changes === 0}
            onClick={onPrimary}
          >
            {recordingOnly
              ? t('resources.import.recordChanges', { n: changes })
              : t('resources.import.confirm', { n: escolhidos.length })}
          </LoadingButton>
        </DrawerFooter>
      }
    >
      {sites.length > 0 ? (
        <div className={s.importSite}>
          <Field label={t('resources.import.site.label')} help={t('resources.import.site.help')}>
            {({ className: _unused, ...props }) => (
              <SearchCombobox
                {...props}
                label={t('resources.import.site.label')}
                options={sites.map((option) => ({
                  value: option.id,
                  label: option.slug,
                  mono: true,
                }))}
                value={site}
                onChange={(escolhido) => setSite(escolhido ?? null)}
                placeholder={t('resources.import.site.placeholder')}
                disabled={preview.isPending || confirm.isPending}
              />
            )}
          </Field>
        </div>
      ) : null}

      {erro ? (
        // FR-123: o erro fica no drawer e nada é importado (cenário 3.8).
        //
        // Leitura e escrita falham por motivos diferentes e se corrigem de
        // maneiras diferentes: o título e o "Tentar de novo" seguem qual das
        // duas falhou, senão o botão refaz a prévia quando quem falhou foi a
        // importação — e o usuário lê "a leitura falhou" depois de confirmar.
        <ErrorBanner
          error={erro}
          escrita={falhouAoImportar}
          title={t(
            falhouAoImportar ? 'resources.import.error.import' : 'resources.import.error.read',
          )}
          onRetry={() => {
            setErro(null);
            if (falhouAoImportar) void confirmImport();
            // O retry relê o MESMO site que a prévia leu — refazer sem ele
            // devolveria o gateway padrão e a grade trocaria de cluster sozinha.
            else preview.mutate(site);
          }}
          retryLabel={t('resources.import.retry')}
        />
      ) : null}

      {preview.isPending ? <p className={s.carregando}>{t('resources.import.loading')}</p> : null}

      {!preview.isPending && listing ? (
        <Alert tone="warning" role="status">
          {t('resources.import.gatewayListingCut', {
            pages: listing.pages,
            n: listing.read,
            last: listing.last ?? '—',
          })}
        </Alert>
      ) : null}

      {!preview.isPending && !erro && itens.length === 0 ? (
        <p className={s.vazioTexto}>{t('resources.import.empty')}</p>
      ) : null}

      {/* O gateway não lista nada, e o console tem recursos dele (aim-6ph.19): sem o aviso, a tela mostrava só linhas
          desabilitadas e um botão apagado, sem dizer por quê. */}
      {!preview.isPending && !erro && gatewayEmpty ? (
        <Alert tone="warning" title={t('resources.import.gatewayEmpty.title')}>
          <span data-testid="importar-gateway-vazio">
            {tn('resources.import.gatewayEmpty.body', itens.length)}
          </span>
        </Alert>
      ) : null}

      {itens.length > 0 ? (
        <div className={s.previaFiltro}>
          <LocalSearchField
            value={filter}
            onChange={setFiltro}
            placeholder={t('resources.import.filterPlaceholder')}
          />
          <span className={s.sub} data-testid="previa-contagem">
            {filtrando
              ? t('resources.import.count.filtered', {
                  shown: visiveis.length,
                  total: itens.length,
                })
              : t('resources.import.count.found', { n: itens.length })}
            {escolhidos.length > 0
              ? t('resources.import.count.marked', { n: escolhidos.length })
              : ''}
          </span>
        </div>
      ) : null}

      {itens.length > 0 && visiveis.length === 0 ? (
        <p className={s.vazioTexto}>{t('resources.import.noMatch', { term: filter.trim() })}</p>
      ) : null}

      {visiveis.length > 0 ? (
        <table className={s.previa}>
          <caption className={s.previaCaption}>{t('resources.import.table.caption')}</caption>
          <thead>
            <tr>
              <th scope="col" aria-label={t('resources.import.column.select')} />
              <th scope="col">{t('resources.import.column.identifier')}</th>
              <th scope="col">{t('resources.import.column.kind')}</th>
              <th scope="col">{t('resources.import.column.itemsOrigin')}</th>
              <th scope="col">{t('resources.import.column.tools')}</th>
              <th scope="col">{t('resources.import.column.situation')}</th>
            </tr>
          </thead>
          <tbody>
            {visiveis.map((item) => {
              const canImport = importavel(item);
              return (
                <tr
                  key={item.gateway_id}
                  data-testid={`previa-${item.gateway_id}`}
                  // Situação e tipo como dado (os valores do contrato), não como
                  // o rótulo acentuado da célula: é sobre eles que o E2E de T174
                  // afirma.
                  data-situacao={item.situation}
                  data-tipo={item.kind}
                >
                  <td>
                    <input
                      type="checkbox"
                      aria-label={t('resources.import.selectRow', { id: item.gateway_id })}
                      checked={selecionados.includes(item.gateway_id)}
                      disabled={!canImport}
                      onChange={() => alternar(item.gateway_id)}
                    />
                  </td>
                  <td>
                    <span className={s.mono}>{item.gateway_id}</span>
                    <LineWarnings item={item} />
                  </td>
                  <td>{kindLabel(item.kind)}</td>
                  <td className={s.previaOrigem}>{item.items_origin ?? '—'}</td>
                  <td>{item.tools_count}</td>
                  <td>
                    <Badge tone={STATUS_TOM[item.situation]}>{statusLabel(item.situation)}</Badge>
                    {/* A marca ainda por registrar, como dado (aim-6ph.19): `saida` ou `volta`. */}
                    {exitToRecord(item) ? (
                      <span className={s.sub} data-mudanca="saida">
                        {t('resources.import.exitToRecord')}
                      </span>
                    ) : returnToRecord(item) ? (
                      <span className={s.sub} data-mudanca="volta">
                        {t('resources.import.returnToRecord')}
                      </span>
                    ) : canImport ? null : (
                      <span className={s.sub}>{t('resources.import.keptInCatalog')}</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      ) : null}

      {/* Fora do padrão: o MESMO site da prévia, relido a cada troca (a chave da query o tem). */}
      {!preview.isPending ? <UngovernedSection siteId={site} /> : null}

      {/* A confirmação do gateway vazio (aim-6ph.19, D2): «tudo saiu» é o caso que um gateway errado em
          Infraestrutura imita, e por isso ele pede um passo a mais. O caso parcial segue com um clique. */}
      <ConfirmDialog
        open={confirmingEmpty}
        title={tn('resources.import.confirmEmpty.title', exits)}
        message={
          <span data-testid="confirmar-gateway-vazio">
            {tn('resources.import.confirmEmpty.body', exits, {
              gateway: gatewayName(infra.data) ?? t('resources.import.gatewayUnknown'),
            })}{' '}
            {ungovernedCount > 0 ? (
              <>
                {tn('resources.import.confirmEmpty.ungoverned', ungovernedCount)}{' '}
              </>
            ) : null}
            {t('resources.import.confirmEmpty.hint')}
          </span>
        }
        confirmLabel={t('resources.import.confirmEmpty.confirm')}
        loading={confirm.isPending}
        onConfirm={() => confirmImport()}
        onCancel={() => setConfirmingEmpty(false)}
      />
    </Drawer>
  );
}

/** Os avisos da linha, um por código, com o código como dado (para o E2E não casar a frase). */
function LineWarnings({ item }: { item: ImportPreviewItem }) {
  const warnings = lineWarnings(item);
  if (warnings.length === 0) return null;
  return (
    <ul className={s.previaAvisos}>
      {warnings.map((warning) => (
        <li key={warning.code} data-testid="previa-aviso" data-code={warning.code}>
          {warning.text}
        </li>
      ))}
    </ul>
  );
}
